import { isEmpty, snakeCase } from 'lodash';
import { Datatype } from '../../../../../expressions/datatype/Datatype.ts';
import { type IWidgetAdvancedOption } from '../../../../Widget.ts';
import { type IWidgetCsvConfig } from '../../common/option-list/WidgetCsvField.ts';
import { type IAdvancedSelectColumns } from '../IAdvancedSelectColumns.ts';
import { normalizeColumnValue } from '../utils/normalizeColumnValue.ts';

export interface IImportCsvOptionsResult {
  columns: IAdvancedSelectColumns[];
  options: IWidgetAdvancedOption[];
}

/**
 * Importa opzioni da CSV per il widget AdvancedSingleSelect.
 */
export function importCsvOptions(
  config: IWidgetCsvConfig,
  firstRowAsHeaders: boolean,
  existingColumns: IAdvancedSelectColumns[] = [],
): IImportCsvOptionsResult {
  if (isEmpty(config.combinations)) {
    throw new Error('Nessun dato CSV da importare.');
  }

  const hasValidData = config.combinations.some(row =>
    row.values.some(val => val.trim() !== ''),
  );

  if (!hasValidData) {
    // biome-ignore format: <tbd>
    throw new Error('Nessun dato valido trovato nel CSV. Assicurati che il file contenga almeno una riga con valori non vuoti.');
  }

  const { columns, dataRows, skipIdGeneration } = firstRowAsHeaders
    ? createColumnsFromFirstRow(config.combinations, existingColumns)
    : getExistingColumnsWithData(config.combinations, existingColumns);

  validateDataRows(dataRows);

  const options = createOptionsFromRows(dataRows, columns, {
    skipIdGeneration,
  });

  return {
    columns,
    options,
  };
}

/**
 * Crea una colonna generica posizionale (id `colonna_N`, label `Colonna N`).
 */
function createGenericColumn(index: number): IAdvancedSelectColumns {
  return {
    id: `colonna_${index}`,
    label: `Colonna ${index}`,
    type: Datatype.String,
  };
}

/**
 * Crea colonne generiche basate sul numero di colonne nel CSV.
 */
function getExistingColumnsWithData(
  combinations: IWidgetCsvConfig['combinations'],
  existingColumns: IAdvancedSelectColumns[],
): {
  columns: IAdvancedSelectColumns[];
  dataRows: IWidgetCsvConfig['combinations'];
  skipIdGeneration?: false;
} {
  const firstRow = combinations[0];
  if (!firstRow || isEmpty(firstRow.values)) {
    throw new Error('Nessun dato trovato nel CSV.');
  }

  const columns: IAdvancedSelectColumns[] =
    existingColumns.length > 0
      ? appendExtraGenericColumns(existingColumns, firstRow.values.length)
      : [
          {
            id: 'id',
            label: 'ID',
            type: Datatype.String,
          },
          ...firstRow.values.map((_, index) => createGenericColumn(index + 1)),
        ];

  return {
    columns,
    dataRows: combinations,
    // Non possiamo sapere se l'utente fornirà un ID univoco, non abbiamo gli Header
    skipIdGeneration: false,
  };
}

/**
 * Appende colonne generiche quando il CSV ha più valori per riga delle colonne
 * dati configurate (esclusa la colonna ID generata), per non perdere i valori
 * in eccesso nel mapping posizionale.
 */
function appendExtraGenericColumns(
  existingColumns: IAdvancedSelectColumns[],
  csvColumnsCount: number,
): IAdvancedSelectColumns[] {
  const dataColumnsCount = existingColumns.filter(
    col => col.id !== 'id',
  ).length;
  const extraCount = csvColumnsCount - dataColumnsCount;

  if (extraCount <= 0) {
    return existingColumns;
  }

  const existingIds = new Set(existingColumns.map(col => col.id));
  const extraColumns: IAdvancedSelectColumns[] = [];

  // Primo indice libero per evitare collisioni con colonna_N già configurate
  let index = 1;
  while (extraColumns.length < extraCount) {
    const column = createGenericColumn(index);
    if (!existingIds.has(column.id)) {
      extraColumns.push(column);
    }
    index += 1;
  }

  return [...existingColumns, ...extraColumns];
}

/**
 * Crea le colonne dalla prima riga del CSV (headers).
 */
function createColumnsFromFirstRow(
  combinations: IWidgetCsvConfig['combinations'],
  existingColumns: IAdvancedSelectColumns[],
): {
  columns: IAdvancedSelectColumns[];
  dataRows: IWidgetCsvConfig['combinations'];
  skipIdGeneration?: boolean;
} {
  const firstRow = combinations[0];
  const headers = firstRow.values.map(h => h.trim()).filter(h => h !== '');

  if (isEmpty(headers)) {
    // biome-ignore format: <tbd>
    throw new Error('Nessun header valido trovato nella prima riga del CSV. Assicurati che la prima riga contenga i nomi delle colonne.');
  }

  const hasIdColumn = headers.some(h => h.toLowerCase() === 'id');

  const uniqueHeaders = new Set<string>(headers.map(snakeCase));
  if (uniqueHeaders.size < headers.length) {
    // biome-ignore format: log
    throw new Error('Gli header delle colonne nel CSV non sono univoci dopo la normalizzazione. Assicurati che ogni colonna abbia un nome univoco.');
  }

  const idColumn: IAdvancedSelectColumns[] = hasIdColumn
    ? []
    : [{ id: 'id', label: 'ID', type: Datatype.String }];

  const existingByHeader = matchExistingColumnsByHeader(
    existingColumns,
    headers,
  );

  const columns: IAdvancedSelectColumns[] = [
    ...idColumn,
    ...headers.map<IAdvancedSelectColumns>(header => {
      const existingColumn = existingByHeader.get(header);
      return {
        id: existingColumn?.id ?? snakeCase(header),
        label: existingColumn?.label ?? header,
        type: existingColumn?.type ?? Datatype.String,
      };
    }),
  ];

  const columnIds = new Set(columns.map(col => col.id));
  if (columnIds.size < columns.length) {
    // biome-ignore format: <tbd>
    throw new Error("Le colonne risultanti dall'import CSV hanno ID duplicati. Verifica che le colonne configurate abbiano ID univoci.");
  }

  return {
    columns,
    dataRows: combinations.slice(1),
    // Saltiamo la generazione degli ID se l'utente li fornisce
    skipIdGeneration: hasIdColumn,
  };
}

/**
 * Associa gli header alle colonne esistenti in due passate (prima match
 * esatto per id, poi per label), escludendo le colonne già assegnate per
 * evitare che due header risolvano la stessa colonna.
 */
function matchExistingColumnsByHeader(
  existingColumns: IAdvancedSelectColumns[],
  headers: string[],
): Map<string, IAdvancedSelectColumns> {
  const matches = new Map<string, IAdvancedSelectColumns>();
  const assigned = new Set<IAdvancedSelectColumns>();

  for (const header of headers) {
    const normalizedHeaderId = snakeCase(header);
    const column = existingColumns.find(
      col => !assigned.has(col) && col.id === normalizedHeaderId,
    );
    if (column) {
      matches.set(header, column);
      assigned.add(column);
    }
  }

  for (const header of headers) {
    if (matches.has(header)) continue;
    const normalizedHeaderLabel = header.trim().toLowerCase();
    const column = existingColumns.find(
      col =>
        !assigned.has(col) &&
        col.label.trim().toLowerCase() === normalizedHeaderLabel,
    );
    if (column) {
      matches.set(header, column);
      assigned.add(column);
    }
  }

  return matches;
}

/**
 * Valida che ogni riga abbia almeno un valore non vuoto.
 */
function validateDataRows(dataRows: IWidgetCsvConfig['combinations']): void {
  dataRows.forEach((row, rowIndex) => {
    const hasValue = row.values.some(val => val.trim() !== '');
    if (!hasValue) {
      // biome-ignore format: <tbd>
      throw new Error(`Riga ${rowIndex + 1} del CSV è vuota. Tutte le righe devono contenere almeno un valore.`);
    }
  });
}

/**
 * Crea le opzioni dalle righe di dati CSV.
 */
function createOptionsFromRows(
  dataRows: IWidgetCsvConfig['combinations'],
  columns: IAdvancedSelectColumns[],
  options: {
    /**
     * Utile se l'utente vuole fornire i propri ID univoci per le opzioni.
     */
    skipIdGeneration?: boolean;
  },
): IWidgetAdvancedOption[] {
  return dataRows.map((row, rowIndex) => {
    const optionId = String(rowIndex + 1);

    // Mappa i valori della riga alle colonne (escludendo la colonna ID)
    const dataColumns = options.skipIdGeneration
      ? columns
      : columns.filter(col => col.id !== 'id');

    const values = dataColumns.reduce<Record<string, string>>(
      (acc, column, colIndex) => {
        if (colIndex < row.values.length) {
          const value = row.values[colIndex].trim();
          if (value !== '') {
            acc[column.id] = normalizeColumnValue(value, column.type);
          }
        }
        return acc;
      },
      {},
    );

    // Valida che la riga abbia almeno un valore mappato (prima di aggiungere l'ID)
    if (isEmpty(values)) {
      // biome-ignore format: <tbd>
      throw new Error(`Riga ${rowIndex + 1} del CSV non contiene valori validi per le colonne configurate.`);
    }

    // Aggiungiamo un carattere per bypassare inizialmente la verifica sull'ID
    // per le regole di visibilità/abilitazione
    if (!options.skipIdGeneration) {
      values.id = `O${optionId}`;
    }

    return {
      type: 'option' as const,
      values,
      visible: { type: 'static' as const, value: true },
      enabled: { type: 'static' as const, value: true },
    };
  });
}
